import { useEffect, useRef, useState } from 'react'
import { Col, Container, Modal, Row, Table, Button, Pagination } from 'react-bootstrap'
import weather from '../services/weather'
import { get } from 'lodash'

const arrowColors = [
  { maxWind: 3, maxWave: 0.25, color: 'rgb(209,209,209)' },
  { maxWind: 6, maxWave: 0.5, color: 'rgb(190, 234, 255)' },
  { maxWind: 8, maxWave: 1, color: 'rgb(149, 221, 255)' },
  { maxWind: 10, maxWave: 1.5, color: 'rgb(163, 204, 163)' },
  { maxWind: 14, maxWave: 2, color: 'rgb(255, 235, 51)' },
  { maxWind: 18, maxWave: 3, color: 'rgb(255, 160, 51)' },
  { maxWind: Infinity, maxWave: Infinity, color: 'rgb(244, 75, 51)' },
]

const weekdayNames = [
  ['SU', 'Sunnuntai'],
  ['MA', 'Maanantai'],
  ['TI', 'Tiistai'],
  ['KE', 'Keskiviikko'],
  ['TO', 'Torstai'],
  ['PE', 'Perjantai'],
  ['LA', 'Lauantai'],
]

const WeatherModal = ({ showWeatherModal, setShowWeatherModal, weatherData }) => {
  const initialWeatherData = useRef(null)
  const [filteredWeatherData, setFilteredWeatherData] = useState(null)
  const [activeDay, setActiveDay] = useState(null)

  useEffect(() => {
    if (showWeatherModal) {
      const filteredInitialWeatherData = filterInitialWeatherData()
      initialWeatherData.current = filteredInitialWeatherData
      handleActiveDayChange(new Date(filteredInitialWeatherData[0].timestamp).getDay())
    }
  }, [showWeatherModal, weatherData])

  const handleActiveDayChange = (activeDay) => {
    setFilteredWeatherData(
      initialWeatherData.current.filter((item) => {
        return new Date(item.timestamp).getDay() === activeDay
      }),
    )

    setActiveDay(activeDay)
  }
  const filterInitialWeatherData = () => {
    const currentUTCTime = Date.now()

    const currentHourIndex = weatherData.forecast.findIndex((item) => {
      const itemTime = new Date(item.timestamp).getTime()
      return itemTime >= currentUTCTime
    })

    const sliceStart = currentHourIndex > 0 ? currentHourIndex - 1 : currentHourIndex

    return weatherData.forecast.slice(sliceStart)
  }

  const DayButtons = () => {
    const uniqueDays = []

    const getDate = (timestamp) => {
      const date = new Date(timestamp).toLocaleDateString('fi-FI', { timeZone: 'Europe/Helsinki' })
      return date
    }

    const getDayIndex = (timestamp) => {
      return new Date(timestamp).getDay()
    }

    initialWeatherData.current.forEach((item) => {
      const day = uniqueDays.find((uniqueDay) => {
        return getDate(uniqueDay) === getDate(item.timestamp)
      })
      if (!day) {
        uniqueDays.push(item.timestamp)
      }
    })

    return (
      <Pagination className="mb-0" size="sm">
        {uniqueDays.map((day) => {
          const dayIndex = getDayIndex(day)
          return (
            <Pagination.Item
              className="mx-1 text-center"
              style={{ minWidth: '1.5em' }}
              key={day}
              active={dayIndex === activeDay}
              onClick={() => handleActiveDayChange(dayIndex)}
            >
              {weekdayNames[dayIndex][0]}
            </Pagination.Item>
          )
        })}
      </Pagination>
    )
  }

  const getTitleDate = (filteredWeatherData) => {
    const startDay = new Date(filteredWeatherData[0].timestamp).toLocaleDateString('fi-FI', {
      day: 'numeric',
      month: 'long',
    })
    const startWeekday = weekdayNames[new Date(filteredWeatherData[0].timestamp).getDay()][1]

    return `${startWeekday} ${startDay}`
  }

  const getTime = (timestamp) => {
    const time = new Date(timestamp).toLocaleString('fi-FI', {
      hour: 'numeric',
      timeZone: 'Europe/Helsinki',
    })

    return time
  }

  const getSmartSymbol = (symbol) => {
    return <img src={`https://cdn.fmi.fi/symbol-images/smartsymbol/v3/p/${symbol}.png`} alt="Weather Symbol" />
  }

  const getArrow = (type, direction, value) => {
    const getArrowColor = (type, value) => {
      for (const item of arrowColors) {
        if (type === 'wind') {
          if (value < item.maxWind) {
            return item.color
          }
          continue
        } else if (type === 'wave') {
          if (value < item.maxWave) {
            return item.color
          }
          continue
        }
      }
    }

    return (
      <i
        className="bi bi-cursor-fill"
        style={{
          transform: `rotate(${direction + 135}deg)`, // Arrow icon points to the top-left by default, so we add 135 degrees
          display: 'inline-block',
          color: getArrowColor(type, value),
        }}
      />
    )
  }

  const getWind = (weather) => {
    return (
      <Container className="px-0">
        <Row>
          <Col className="ps-0 pe-2 text-end align-self-center">
            {getArrow('wind', weather.windDirection, Math.round(weather.windSpeed))}
          </Col>
          <Col className="ps-0">
            <div className="">{Math.round(weather.windSpeed)}</div>
            <div className="">{`(${Math.round(weather.windGust)})`}</div>
          </Col>
        </Row>
      </Container>
    )
  }

  const getWave = (waves) => {
    const waveHight = waves.significantHeight ? waves.significantHeight.toFixed(1).replace('.', ',') : '-'
    const waveDirectionArrow = waves.direction ? getArrow('wave', waves.direction, waves.significantHeight ?? 0) : '-'

    return (
      <Container className="px-1">
        <Row className="">
          <Col className="px-1 text-end">{waveDirectionArrow}</Col>
          <Col className="px-1">{waveHight}</Col>
        </Row>
      </Container>
    )
  }

  const waveDataAvailable = initialWeatherData.current
    ? initialWeatherData.current.some((item) => item.waves.direction || item.waves.significantHeight)
    : false

  if (filteredWeatherData) {
    return (
      <Modal show={showWeatherModal} onHide={() => setShowWeatherModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <p className="mb-1">Hylkysää - {weatherData.wreckName}</p>
            <p className="fs-6 fw-normal">{getTitleDate(filteredWeatherData)}</p>
            <DayButtons />
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Table size="sm" className="text-center">
            <thead className="align-middle">
              <tr>
                <th>Kello</th>
                <th>Sää</th>
                <th>Lämpötila</th>
                <th>Tuuli (m/s)</th>
                {waveDataAvailable && <th>Aalto (m)</th>}
              </tr>
            </thead>
            <tbody>
              {filteredWeatherData.map((item) => (
                <tr key={item.timestamp} className="align-middle">
                  <td className="fw-semibold">{getTime(item.timestamp)}</td>
                  <td>{getSmartSymbol(item.weather.smartSymbol)}</td>
                  <td>{`${Math.round(item.weather.temperature)}°`}</td>
                  <td>{getWind(item.weather)}</td>
                  {waveDataAvailable && <td>{getWave(item.waves)}</td>}
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
    )
  }
}

export default WeatherModal
